// Duck/unduck the currently mounted <video> element when voice is playing.
// Reads live from the mixer store (no local copy of the settings — single
// source of truth), and reads the <video> from the module-level singleton in
// src/lib/activeVideo.ts so any caller anywhere in the tree can grab the
// active element without prop-drilling a ref.
//
// As of the 3-channel-mixer rework this hook is the **central ducker** for
// every gain target that should respond to voice playback — currently:
//
//   1. The active <video> element (HTMLMediaElement.volume).
//   2. The Web Audio bed-gain node owned by useTimelinePreview (the SFX/
//      music bed that now plays alongside voice on every asset, including
//      video assets — so video AND bed both duck when voice is audible).
//
// Both targets share the same on/off + strength slider (`videoDucking*`
// fields in the mixer store). Each target reads its own user fader for the
// base value (videoUserVolume vs. sfxVolume), so the channels stay
// independent — only the duck *attenuation* is shared.
//
// Legacy reference: setVideoVolume (index.html:6260), _videoAutoDuck
// (index.html:6280), _videoAutoUnduck (index.html:6289). Pre-rework version
// only handled the <video> element; bed is now a first-class duck target
// via sfxBedRouting.applyBedGainExternalAll().

import { useCallback } from 'react';
import { useMixerStore } from '@/stores/mixer';
import { getActiveVideo, onActiveVideoChange } from '@/lib/activeVideo';
import { applyBedGainExternalAll } from '@/lib/sfxBedRouting';
import { linearRampValue } from '@/lib/duckAutomation';
import {
  VIDEO_DUCKING_ATTACK_MS,
  VIDEO_DUCKING_RELEASE_MS,
} from '@shared/constants';

// Module-level "is voice currently audible" flag. Lifted out of the hook so
// every consumer (preview hooks + MixerPopover) shares the same source of
// truth — the live store-subscription below otherwise had a race where a
// non-playing instance's local ref (=false) would overwrite the playing
// instance's ducked gain back to its non-ducked level on every store write.
// Only one preview hook ever drives playback at a time, so a single flag is
// sufficient.
let moduleIsDucked = false;

// rAF handle for the in-flight video.volume ramp. HTMLMediaElement.volume
// has no native scheduled-ramp API like Web Audio AudioParam — we animate
// it manually here. Cancelled and restarted on each transition so a fast
// duck/unduck cycle never overshoots.
let videoRampRaf: number | null = null;

/** Animate a <video>'s .volume linearly from its current value to `target`
 *  over `ms`. `ms = 0` snaps instantly. Cancels any in-flight ramp first. */
function rampVideoVolume(v: HTMLVideoElement, target: number, ms: number): void {
  if (videoRampRaf !== null) {
    cancelAnimationFrame(videoRampRaf);
    videoRampRaf = null;
  }
  if (ms <= 0) {
    v.volume = target;
    return;
  }
  const start = v.volume;
  const startT = performance.now();
  const tick = (now: number) => {
    const elapsed = now - startT;
    // Dieselbe Interpolation wie die Export-Hüllkurve (duckAutomation.ts).
    v.volume = linearRampValue(start, target, elapsed, ms);
    if (elapsed < ms) {
      videoRampRaf = requestAnimationFrame(tick);
    } else {
      videoRampRaf = null;
    }
  };
  videoRampRaf = requestAnimationFrame(tick);
}

/** Push the current mixer state to both duck targets, reading from
 *  `moduleIsDucked` so every caller (duck/unduck/store-subscription) lands
 *  on the same effective gain. `rampMs` controls smoothing — `0` for
 *  instant slider drags, attack/release ms on actual duck transitions. */
function reapplyTargets(rampMs = 0): void {
  const ducked = moduleIsDucked;
  applyBedGainExternalAll(ducked, rampMs);
  const v = getActiveVideo();
  if (!v) return;
  const { videoUserVolume, videoMuted, videoDuckingEnabled, videoDuckingStrength } =
    useMixerStore.getState();
  v.muted = videoMuted || videoUserVolume === 0;
  const target =
    ducked && videoDuckingEnabled
      ? videoUserVolume * (1 - videoDuckingStrength / 100)
      : videoUserVolume;
  rampVideoVolume(v, target, rampMs);
}

// Module-level live subscription. Wired once on first import so duck-config
// changes (Auto-Duck toggle + strength slider) AND user-fader changes
// propagate to the audio targets immediately, regardless of which component
// holds a useVideoDucking() instance. Mirrors the voice-fader live behaviour
// in audioContext.ts:ensureVoiceGain.
//
// Without this, two failure modes existed:
//   1. Toggling Auto-Duck ON while voice was audible mid-block did nothing —
//      the rAF edge-detector had already passed through the
//      lastActiveBlock=current state, so duck() was never re-fired and the
//      ducked gain never landed until the NEXT block boundary.
//   2. Dragging the strength slider only worked when MixerPopover was
//      mounted (because its onChange handler manually called
//      applyVideoVolume/applySfxVolume). Any future caller mutating the
//      store would silently fail to update audio.
//
// Subscribing at module-scope deduplicates: previously each hook instance
// (Timeline / STS / MixerPopover) registered its own listener and a
// non-playing instance with isDuckedRef=false could win the last write,
// stomping the actively-ducked gain.
// Active-video changes also need to push the current mixer values onto
// the freshly-mounted <video> element. Without this the browser's default
// volume (1.0) wins and the user only hears the mixer level after the
// first fader touch (#168). The store-subscribe path covers mid-session
// changes; this one covers the asset-switch / first-mount transition.
onActiveVideoChange(() => reapplyTargets());

let prevMixerSnapshot = useMixerStore.getState();
useMixerStore.subscribe((s) => {
  const changed =
    s.videoDuckingEnabled !== prevMixerSnapshot.videoDuckingEnabled ||
    s.videoDuckingStrength !== prevMixerSnapshot.videoDuckingStrength ||
    s.videoUserVolume !== prevMixerSnapshot.videoUserVolume ||
    s.videoMuted !== prevMixerSnapshot.videoMuted ||
    s.sfxVolumes[0] !== prevMixerSnapshot.sfxVolumes[0] ||
    s.sfxVolumes[1] !== prevMixerSnapshot.sfxVolumes[1] ||
    s.sfxMuted[0] !== prevMixerSnapshot.sfxMuted[0] ||
    s.sfxMuted[1] !== prevMixerSnapshot.sfxMuted[1];
  prevMixerSnapshot = s;
  if (!changed) return;
  reapplyTargets();
});

export function useVideoDucking() {
  const duck = useCallback(() => {
    moduleIsDucked = true;
    reapplyTargets(VIDEO_DUCKING_ATTACK_MS);
  }, []);

  const unduck = useCallback(() => {
    moduleIsDucked = false;
    reapplyTargets(VIDEO_DUCKING_RELEASE_MS);
  }, []);

  const isDucked = useCallback(() => moduleIsDucked, []);

  /** Re-apply the **video** mixer settings to the <video> element + bed.
   *  Kept as a callable for back-compat with MixerPopover's onChange
   *  handlers — these are now redundant with the module-level subscription
   *  above, but keeping the API stable means we don't have to touch every
   *  consumer in this change. */
  const applyVideoVolume = useCallback(() => {
    reapplyTargets();
  }, []);

  /** Re-apply the **SFX** mixer settings to the bed gain. Same back-compat
   *  rationale as applyVideoVolume. */
  const applySfxVolume = useCallback(() => {
    applyBedGainExternalAll(moduleIsDucked);
  }, []);

  return { duck, unduck, isDucked, applyVideoVolume, applySfxVolume };
}
